import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

const theme = createTheme({
  palette: {
    primary: {
      main: '#635bff',
      light: '#7c75ff',
      dark: '#5a52e8',
    },
    secondary: {
      main: '#00d4aa',
      light: '#26e5c7',
      dark: '#00c29a',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#0a2540',
      secondary: '#425466',
    },
    grey: {
      50: '#fafbfc',
      100: '#f6f9fc',
      200: '#e3e8ee',
      300: '#c7d2fe',
      400: '#8792a2',
      500: '#697386',
      600: '#425466',
      700: '#3c4257',
      800: '#1e2329',
      900: '#0a2540',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '80px',
      lineHeight: '88px',
      letterSpacing: '-0.025em',
      '@media (max-width:960px)': {
        fontSize: '48px',
        lineHeight: '56px',
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: '48px',
      lineHeight: '56px',
      letterSpacing: '-0.02em',
      '@media (max-width:960px)': {
        fontSize: '32px',
        lineHeight: '40px',
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '40px',
      lineHeight: '48px',
      letterSpacing: '-0.02em',
      '@media (max-width:960px)': {
        fontSize: '28px',
        lineHeight: '36px',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '32px',
      lineHeight: '40px',
      letterSpacing: '-0.015em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '32px',
      '@media (max-width:960px)': {
        fontSize: '20px',
        lineHeight: '28px',
      },
    },
    h6: {
      fontWeight: 400,
      fontSize: '20px',
      lineHeight: '32px',
      '@media (max-width:960px)': {
        fontSize: '18px',
        lineHeight: '28px',
      },
    },
    body1: {
      fontSize: '16px',
      lineHeight: '24px',
      color: '#425466',
    },
    body2: {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#8792a2',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '12px 24px',
          fontSize: '0.95rem',
          fontWeight: 500,
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          backgroundColor: '#667eea',
          color: 'white',
          '&:hover': {
            backgroundColor: '#4c63d2',
          },
        },
        outlined: {
          borderColor: '#e2e8f0',
          color: '#475569',
          '&:hover': {
            borderColor: '#cbd5e1',
            backgroundColor: '#f8fafc',
          },
        },
        text: {
          color: '#667eea',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: '#cbd5e1',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
        elevation3: {
          boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
              borderColor: '#cbd5e1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#667eea',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.875rem',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);